package com.writerslab.office

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLAnchorElement

@Composable
fun ReportsPage() {
    var books by remember { mutableStateOf<List<OfficeBook>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    // Kept apart from [error]: a failed download must not replace the table.
    var downloadError by remember { mutableStateOf<String?>(null) }
    var downloadingKey by remember { mutableStateOf<String?>(null) }
    var includeAnon by remember { mutableStateOf(false) }
    // Bumped by «Обновить». Both reload triggers are keys of one effect, so a
    // new load cancels the one in flight instead of racing it.
    var reloads by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(includeAnon, reloads) {
        error = null; books = null
        if (PREVIEW) {
            books = MockApi.books().filter { includeAnon || it.userId != ANON_USER }
            return@LaunchedEffect
        }
        try { books = ApiClient.getBooks(includeAnon) }
        catch (e: Throwable) { error = e.message ?: "Ошибка загрузки" }
    }

    // [key] marks the button showing «…» while the ticket is fetched; after
    // that the browser downloads the file by itself.
    fun download(key: String, path: String) {
        if (downloadingKey != null) return
        scope.launch {
            downloadingKey = key
            try {
                downloadError = null
                if (PREVIEW) return@launch
                startBrowserDownload(ApiClient.downloadUrl(path))
            } catch (e: Throwable) {
                downloadError = "Не удалось начать скачивание: ${e.message ?: "ошибка сети"}"
            } finally { downloadingKey = null }
        }
    }

    Div({ style { property("padding", "24px 0") } }) {
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "12px")
                marginBottom(20.px)
            }
        }) {
            H2({ style { color(Color(C.OnSurface)); property("font-size", "18px"); margin(0.px) } }) {
                Text("Рукописи и отчёты")
            }
            SmallButton("Обновить") { reloads++ }
            Label(attrs = {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "6px")
                    color(Color(C.Muted))
                    fontSize(13.px)
                    property("cursor", "pointer")
                }
            }) {
                Input(InputType.Checkbox) {
                    checked(includeAnon)
                    onChange { includeAnon = it.value }
                }
                Text("Включая анонимные")
            }
        }

        if (error != null) {
            P({ style { color(Color(C.Error)) } }) { Text(error!!) }
            return@Div
        }
        if (books == null) {
            P({ style { color(Color(C.Muted)) } }) { Text("Загрузка…") }
            return@Div
        }

        val list = books!!
        // Bulk exports follow the checkbox, so they cover exactly the list on screen.
        Div({ style { card(); marginBottom(20.px) } }) {
            Div({ style { fontSize(14.px); property("font-weight", "600"); marginBottom(4.px) } }) {
                Text("Скачать все книги одним архивом")
            }
            Div({ style { color(Color(C.Muted)); fontSize(12.px); marginBottom(12.px) } }) {
                Text(
                    "${list.size} книг" + (if (includeAnon) ", включая анонимные" else ", без анонимных") +
                        ". Архив собирается на лету — прогресс виден в загрузках браузера."
                )
            }
            Div({
                style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "8px")
                }
            }) {
                EXPORTS.forEach { e ->
                    val key = "export/${e.content}"
                    ExportCard(
                        title = e.title,
                        hint = e.hint,
                        busy = downloadingKey == key,
                        disabled = downloadingKey != null || list.isEmpty(),
                    ) {
                        download(key, "/books/export?content=${e.content}&includeAnon=$includeAnon")
                    }
                }
            }
        }

        downloadError?.let { msg ->
            P({ style { color(Color(C.Error)); marginBottom(12.px) } }) { Text(msg) }
        }

        Div({ style { property("overflow-x", "auto") } }) {
            Table({
                style {
                    width(100.percent)
                    property("border-collapse", "collapse")
                    color(Color(C.OnSurface))
                    fontSize(13.px)
                }
            }) {
                Thead {
                    Tr {
                        Th({ attr("colspan", "5") }) {}
                        Th({
                            attr("colspan", "4")
                            style {
                                property("text-align", "left")
                                property("border-bottom", "1px solid ${C.Border}")
                                color(Color(C.Muted))
                            }
                        }) { Text("Скачать книгу") }
                    }
                    Tr {
                        Th { Text("Пользователь") }
                        Th { Text("Файл") }
                        Th { Text("Слов") }
                        Th { Text("AI") }
                        Th { Text("Дата") }
                        Th({ attr("title", EXPORT_ALL.hint) }) { Text("Всё") }
                        Th({ attr("title", "Текст рукописи, .txt") }) { Text("Рукопись") }
                        Th({ attr("title", "Отчёт статического анализа, .json") }) { Text("Отчёт: статика") }
                        Th({ attr("title", "Отчёт AI-анализа (pipeline), .json") }) { Text("Отчёт: pipeline") }
                    }
                }
                Tbody {
                    list.forEach { b ->
                        val staticKey = "${b.userId}/${b.fingerprint}/static"
                        val pipeKey = "${b.userId}/${b.fingerprint}/pipeline"
                        val textKey = "${b.userId}/${b.fingerprint}/manuscript"
                        val bundleKey = "${b.userId}/${b.fingerprint}/bundle"
                        Tr {
                            Td {
                                when {
                                    b.userId == ANON_USER -> Span({ style { color(Color(C.Muted)) } }) { Text("аноним") }
                                    b.email != null -> Span({ attr("title", b.userId) }) { Text(b.email) }
                                    else -> Span({ style { mono(); fontSize(11.px); color(Color(C.Muted)) } }) {
                                        Text(b.userId.take(12) + "…")
                                    }
                                }
                            }
                            Td {
                                Span({
                                    style {
                                        property("max-width", "200px")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                        property("display", "block")
                                    }
                                    attr("title", b.filename)
                                }) { Text(b.filename) }
                            }
                            Td { Text(b.wordCount.toString()) }
                            Td {
                                Span({
                                    style {
                                        fontSize(11.px)
                                        property("font-weight", "600")
                                        color(Color(if (b.hasAiResult) C.Success else C.Muted))
                                    }
                                }) { Text(if (b.hasAiResult) "✓" else "—") }
                            }
                            Td { Text(b.analyzedAt.take(10)) }
                            Td {
                                DownloadBtn(
                                    label = if (downloadingKey == bundleKey) "…" else "↓ ZIP",
                                    disabled = downloadingKey != null,
                                ) {
                                    download(bundleKey, "/books/${b.userId}/${b.fingerprint}/bundle")
                                }
                            }
                            Td {
                                if (b.hasText) {
                                    DownloadBtn(
                                        label = if (downloadingKey == textKey) "…" else "↓ TXT",
                                        disabled = downloadingKey != null,
                                    ) {
                                        download(textKey, "/books/${b.userId}/${b.fingerprint}/manuscript")
                                    }
                                } else {
                                    Span({ style { color(Color(C.Muted)) } }) { Text("нет") }
                                }
                            }
                            Td {
                                if (b.hasStaticReport) {
                                    DownloadBtn(
                                        label = if (downloadingKey == staticKey) "…" else "↓ JSON",
                                        disabled = downloadingKey != null,
                                    ) {
                                        download(staticKey, "/books/${b.userId}/${b.fingerprint}/report?type=static")
                                    }
                                } else {
                                    Span({ style { color(Color(C.Muted)) } }) { Text("нет") }
                                }
                            }
                            Td {
                                if (b.hasPipelineReport) {
                                    DownloadBtn(
                                        label = if (downloadingKey == pipeKey) "…" else "↓ JSON",
                                        disabled = downloadingKey != null,
                                    ) {
                                        download(pipeKey, "/books/${b.userId}/${b.fingerprint}/report?type=pipeline")
                                    }
                                } else {
                                    Span({ style { color(Color(C.Muted)) } }) { Text("нет") }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DownloadBtn(label: String, disabled: Boolean, onClick: () -> Unit) {
    Button({
        style {
            btn(C.SurfaceHigh, C.Primary)
            property("border", "1px solid ${C.Border}")
            fontSize(12.px)
            padding(4.px, 8.px)
        }
        if (disabled) attr("disabled", "")
        onClick { onClick() }
    }) { Text(label) }
}

private const val ANON_USER = "_anon"

/** One bulk-export option. The same three words name things across the page:
 *  «рукопись» — the text, «отчёты» — the static and pipeline JSON,
 *  «всё» — the text plus every analysis file the book has. */
private class ExportOption(val content: String, val title: String, val hint: String)

private val EXPORT_ALL = ExportOption("full", "Всё", "Рукопись, отчёты и остальные файлы анализа")

private val EXPORTS = listOf(
    EXPORT_ALL,
    ExportOption("manuscripts", "Рукописи", "Только тексты, .txt"),
    ExportOption("reports", "Отчёты", "Статика и pipeline, .json"),
)

@Composable
private fun ExportCard(title: String, hint: String, busy: Boolean, disabled: Boolean, onClick: () -> Unit) {
    Button({
        style {
            btn(C.SurfaceHigh, C.OnSurface)
            property("border", "1px solid ${C.Border}")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "2px")
            property("min-width", "200px")
            property("text-align", "left")
            padding(10.px, 14.px)
            if (disabled && !busy) property("opacity", "0.5")
        }
        if (disabled) attr("disabled", "")
        onClick { onClick() }
    }) {
        Span({ style { color(Color(C.Primary)) } }) {
            Text(if (busy) "…" else "↓ $title")
        }
        Span({ style { color(Color(C.Muted)); fontSize(11.px); property("font-weight", "400") } }) {
            Text(hint)
        }
    }
}

/** Hands [url] to the browser's own download manager. `download` keeps the
 *  page in place even on an error response, and its empty value lets the
 *  server's Content-Disposition name the file. */
private fun startBrowserDownload(url: String) {
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url
    a.download = ""
    document.body?.appendChild(a)
    a.click()
    document.body?.removeChild(a)
}
